/**
 * C1 v1.4: the Logs tab reads (C2 v1.6 `GET /api/logs/price` and `GET /api/logs/costs`), their URL codec and every
 * sentence of the two panes. Types mirror C2's JSON field for field; money stays the API's string and is only ever
 * handed to `Intl.NumberFormat` through `format.ts`. The only numeric conversion here reads the page index off the URL.
 * C1 v1.14: a Price row carries the rule-verdict snapshot its commit recorded (C2 v1.16), or null.
 * C1 v1.32 (c1-logs-polish, item 7, C2 v1.35): every pane also filters by the module's Product, Variant, Channel and Store
 * (the keys and the words the Price Run writes, so a filter set on either tab reads the same on the other) and by a period
 * (`f_period`, two days joined by ".."); the Costs pane reads the fulfilment standards as the current set or as their
 * changes (`pp_lfulfil`); each kind of entry and change has its own chip; every cost row names where it came from; each
 * pane exports every matching entry as a CSV, written here from C2's export answer in the screen's own words. The fetch is
 * api.ts's `call` (c1-logs-tab D5).
 * C1 v1.36 (c1-place-filters, go-live gap sweep unit 4, C2 v1.37): every pane also filters by the Price Run's Location and
 * Marketplace (the same two keys and words). A Location is a two-letter place; a Marketplace is a marketplace's name, sent as
 * written, or a ship country's name, sent as its code. An entry with no place (a cost entry, a store-wide Shopify price) is not
 * listed under either, and the toolbar says so.
 */
import { call, GRID_STALE_TIME, PricingError, type Flag, type Market } from "./api";
import { ENTITY_LABEL } from "./config";
import { laneLabel, WHOLESALE_LABEL } from "./discounts";
import { COMPONENT_LABEL, countryCode, countryName, FLAG_LABEL, humanDate, MARKET_LABEL, messageFor, personName, stamp, STORE_LABEL } from "./format";
import { enderName } from "./overrides";
import type { StrategySnapshot } from "./strategies";

export type LogPane = "costs" | "price" | "rrp";
export type PriceSubject = "price" | "free_shipping_threshold";
export type PriceSource = "price_run" | "threshold_run" | "legacy_sheet" | "override_return";
export type CostEntryType = "last_production_cogs" | "current_inventory_cogs" | "projected_cogs" | "avg_discount" | "avg_refund" | "avg_ads" | "fulfilment";
export type LogChannel = "amazon" | "shopify" | "wholesale";
export type FulfilmentView = "current" | "changes";
export type LogSku = { xero_sku: string; product: string; variant: string };
export type LogPageInfo = { index: number; size: number; total: number };
/** The products, variants, channels and stores of the SKUs the whole log holds (C2 v1.35; absent from an older API). */
export type PlaceFacets = { products?: string[]; variants?: string[]; channels?: LogChannel[]; stores?: string[]; locations?: string[]; marketplaces?: LogMarketplace[] };
/** A marketplace the log holds (C2 v1.37): `code` is the value the marketplace filter takes (an Amazon marketplace's name, or a
 * ship country's code), `name` the marketplace's name (null for a ship country), `id` the mirror's id (null for a ship country). */
export type LogMarketplace = { code: string; name: string | null; id: string | null };
/** The place filters as C2 v1.35 applied them, and since C2 v1.37 the location and marketplace (absent from an older API). */
export type PlaceFilters = { product?: string | null; variant?: string | null; channel?: string | null; store?: string | null; location?: string | null; marketplace?: string | null };

/** One Price Log row, exactly as C2 v1.16 serves it. A threshold row has no SKU; a legacy row has no previous value; only a
 * Price Run entry committed from C3 v1.7 on carries a strategy snapshot. */
export type PriceLogRow = {
  id: number; subject_type: PriceSubject; xero_sku: string | null; product: string | null; variant: string | null;
  channel_location: string | null; channel: string | null; market: Market | null; marketplace_id: string | null;
  marketplace_name: string | null; country: string | null; new_value: string; previous_value: string | null; currency: string;
  approver: string; source: PriceSource; comment: string | null; run_line_id: number | null; price_run_id: number | null;
  committed_at: string; written_at: string; strategy_snapshot: StrategySnapshot | null;
  // C1 v1.50 (C2 v1.52): a temporary price's pair on its own entry, and on its return the override it closes; absent before v1.52.
  override_reason?: string | null; override_until?: string | null; override_entry_id?: number | null;
};
export type PriceLogFilters = { market: string | null; sku: string | null; subject: string | null; source: string | null; from: string | null; to: string | null } & PlaceFilters;
export type PriceLogFacets = { skus: LogSku[]; markets: Market[]; subjects: PriceSubject[]; sources: PriceSource[] } & PlaceFacets;
export type PriceLogPage = { rows: PriceLogRow[]; page: LogPageInfo; filters: PriceLogFilters; facets: PriceLogFacets };

/** One Costs Log row: a dated cost entry (channel fields null) or a fulfilment standard (its own channel, marketplace, country,
 * component). C2 v1.35 adds where it came from, every field absent from an older API: the value a fulfilment change replaced,
 * the PFC run and its feed day, the PCOGS record with its production key or legacy sheet row, the entity and time of the Xero read. */
export type CostLogRow = {
  xero_sku: string; product: string; variant: string; sku_channel_location: string; market: Market; entry_type: CostEntryType;
  channel_location: string | null; marketplace_id: string | null; marketplace_name: string | null; country: string | null;
  component: string | null; value: string; currency: string; effective_date: string; flag: Flag; source: string; written_at: string;
  previous_value?: string | null; fed_on?: string | null; run_id?: string | null; record_id?: string | null;
  production_key?: string | null; legacy_ref?: string | null; entity?: string | null; read_at?: string | null;
};
export type CostLogFilters = { market: string | null; sku: string | null; entry_type: string | null; flag: string | null; from: string | null; to: string | null; fulfilment?: FulfilmentView } & PlaceFilters;
export type CostLogFacets = { skus: LogSku[]; markets: Market[]; entry_types: CostEntryType[]; flags: Flag[] } & PlaceFacets;
export type CostsLogPage = { rows: CostLogRow[]; page: LogPageInfo; filters: CostLogFilters; facets: CostLogFacets };

export const PANES: LogPane[] = ["costs", "price", "rrp"];
export const SUBJECTS: PriceSubject[] = ["price", "free_shipping_threshold"];
export const PRICE_SOURCES: PriceSource[] = ["price_run", "threshold_run", "legacy_sheet", "override_return"];
export const ENTRY_TYPES: CostEntryType[] = ["last_production_cogs", "current_inventory_cogs", "projected_cogs", "avg_discount", "avg_refund", "avg_ads", "fulfilment"];
export const LOGS_PAGE_SIZE = 50;

/** The tab's pane and page, every filter of the three panes and the period, read from the URL (`pp_l*` and `f_*` keys). */
export type LogsParams = {
  pane: LogPane; page: number; market: string | null; sku: string | null;
  subject: string | null; source: string | null; entryType: string | null; flag: string | null;
  product: string | null; variant: string | null; channel: string | null; store: string | null;
  location: string | null; marketplace: string | null;
  from: string | null; to: string | null; fulfilment: FulfilmentView;
};
const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);
const oneOf = <T extends string>(v: string | null, allowed: readonly T[]): T | null => (v && (allowed as readonly string[]).includes(v) ? (v as T) : null);
const MARKETS: Market[] = ["UK", "EU", "AE"];
/** The fifteen C3 flags at runtime, from the label table (one list, kept by format.ts). */
const FLAGS = Object.keys(FLAG_LABEL);
const PERIOD = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;
const COUNT = new Intl.NumberFormat("en-GB");

/** A real calendar day: "2026-02-31" is none, so a hand-typed day C2 would refuse reads as no period, never as an error. */
const isDay = (day: string): boolean => {
  try { return new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) === day; } catch { return false; }
};
/** `f_period` "2026-09-01..2026-09-30": two real days, the first not after the second, or no period at all. */
export function periodOf(value: string | null): { from: string | null; to: string | null } {
  const m = value ? PERIOD.exec(value) : null;
  return m && m[1] <= m[2] && isDay(m[1]) && isDay(m[2]) ? { from: m[1], to: m[2] } : { from: null, to: null };
}
/** The URL value of a period; the whole log writes nothing. */
export const periodValue = (from: string | null, to: string | null): string | undefined => (from && to ? `${from}..${to}` : undefined);

export function paramsFrom(search: Record<string, unknown>): LogsParams {
  const period = periodOf(str(search.f_period));
  return {
    pane: oneOf(str(search.pp_lpane), PANES) ?? "costs",
    page: Math.max(0, Number(search.pp_lpage ?? 0) || 0),
    market: oneOf(str(search.f_market)?.toUpperCase() ?? null, MARKETS),
    sku: str(search.f_sku),
    subject: oneOf(str(search.f_subject), SUBJECTS),
    source: oneOf(str(search.f_source), PRICE_SOURCES),
    entryType: oneOf(str(search.f_type), ENTRY_TYPES),
    flag: oneOf(str(search.f_flag)?.toUpperCase() ?? null, FLAGS),
    product: str(search.f_product),
    variant: str(search.f_variant),
    channel: str(search.f_channel),
    store: str(search.f_store),
    location: locationOf(str(search.f_location)),
    marketplace: str(search.f_marketplace),
    from: period.from,
    to: period.to,
    fulfilment: search.pp_lfulfil === "changes" ? "changes" : "current",
  };
}
/** The URL for a fulfilment view: the current set writes nothing; the page resets; every filter stays. */
export function searchForFulfilment(search: Record<string, unknown>, view: FulfilmentView): Record<string, unknown> {
  return { ...search, pp_lfulfil: view === "changes" ? "changes" : undefined, pp_lpage: undefined };
}
/** The URL for a period (the whole log writes nothing); the page resets. */
export function searchForLogPeriod(search: Record<string, unknown>, from: string | null, to: string | null): Record<string, unknown> {
  return { ...search, f_period: periodValue(from, to), pp_lpage: undefined };
}
/** The URL for another pane: the default pane writes nothing; the page resets; every filter and the period stay. */
export function searchForPane(search: Record<string, unknown>, pane: LogPane): Record<string, unknown> {
  return { ...search, pp_lpane: pane === "costs" ? undefined : pane, pp_lpage: undefined };
}

export const CHANNEL_LABEL: Record<LogChannel, string> = { amazon: "Amazon", shopify: "Shopify", wholesale: "Wholesale" };
const CHANNEL_OF: Record<string, LogChannel> = { Amazon: "amazon", Shopify: "shopify", Wholesale: "wholesale", amazon: "amazon", shopify: "shopify", wholesale: "wholesale" };
const STORE_OF: Record<string, string> = Object.fromEntries(Object.entries({ ...STORE_LABEL, ...WHOLESALE_LABEL }).map(([code, name]) => [name, code]));
/** A store by its name ("Shopify EU", "Wholesale UK": the Discounts lane names), or its code when it has none. */
export const storeName = (code: string): string => laneLabel(code);
/** The channel a URL value names ("Amazon", as the Price Run writes it, or the code); any other value is sent as written, and C2
 * answers an empty page, as for an unknown store, so the pill that names it and the table agree. */
export const channelCode = (value: string | null): string | null => (value ? CHANNEL_OF[value] ?? value : null);
/** The store a URL value names: a store name maps to its code; any other value is sent as written (C2 answers an empty page). */
export const storeCode = (value: string | null): string | null => (value ? STORE_OF[value] ?? value : null);

/** A Location as the Price Run writes it: two letters, read in capitals; any other value is no location (C2 would refuse it). */
export const locationOf = (value: string | null): string | null => (value && /^[A-Za-z]{2}$/.test(value) ? value.toUpperCase() : null);
/** The marketplace a URL value names: a country's name ("Germany", a ship-country row's Marketplace on the Price Run) is sent as
 * its code; any other value (a marketplace's name, "Amazon.de") as written, and C2 answers an empty page for one it does not hold. */
export const marketplaceCode = (value: string | null): string | null => (value ? countryCode(value) ?? value : null);
/** A marketplace by the name the Price Run filters it by: its own, or its country's. */
export const marketplaceLabel = (m: LogMarketplace): string => m.name ?? countryName(m.code);

/** The place filters and the period, as every pane reads them off the URL. */
export type PlaceParams = Pick<LogsParams, "product" | "variant" | "channel" | "store" | "location" | "marketplace" | "from" | "to">;
/** What a Logs pane's filters narrow (bug 4010402f-c981-dbac): every place key counts, the Location and Marketplace carried
 * over from the Price Run included, so a filter that matches nothing keeps the toolbar and its Clear. A SKU on its own is
 * that SKU's history. `own` is the pane's own filters (Change, Source, Status, Type, Flag). */
export function filterState(p: PlaceParams & { market: string | null; sku: string | null }, own: Array<string | null>): "none" | "sku" | "other" {
  if ([p.market, p.product, p.variant, p.channel, p.store, p.location, p.marketplace, p.from, p.to, ...own].some(Boolean)) return "other";
  return p.sku ? "sku" : "none";
}
/** The place filters and the period of a pane's query string (the RRP pane's too, rrp.ts). */
export function placeQuery(q: URLSearchParams, p: PlaceParams): void {
  if (p.product) q.set("product", p.product);
  if (p.variant) q.set("variant", p.variant);
  const channel = channelCode(p.channel);
  if (channel) q.set("channel", channel);
  const store = storeCode(p.store);
  if (store) q.set("store", store);
  if (p.location) q.set("location", p.location);
  const marketplace = marketplaceCode(p.marketplace);
  if (marketplace) q.set("marketplace", marketplace);
  if (p.from && p.to) { q.set("from", p.from); q.set("to", p.to); }
}
/** One page, or with `all` every matching entry (C2 v1.35 `export=1`), which ignores the page. */
function pageQuery(q: URLSearchParams, p: LogsParams, all: boolean): string {
  if (all) q.set("export", "1");
  else {
    if (p.page > 0) q.set("page", String(p.page));
    q.set("page_size", String(LOGS_PAGE_SIZE));
  }
  return `?${q.toString()}`;
}
function priceQuery(p: LogsParams, all = false): string {
  const q = new URLSearchParams();
  if (p.market) q.set("market", p.market);
  if (p.sku) q.set("sku", p.sku);
  if (p.subject) q.set("subject", p.subject);
  if (p.source) q.set("source", p.source);
  placeQuery(q, p);
  return pageQuery(q, p, all);
}
function costsQuery(p: LogsParams, all = false): string {
  const q = new URLSearchParams();
  if (p.market) q.set("market", p.market);
  if (p.sku) q.set("sku", p.sku);
  if (p.entryType) q.set("entry_type", p.entryType);
  if (p.flag) q.set("flag", p.flag);
  if (p.fulfilment === "changes") q.set("fulfilment", "changes");
  placeQuery(q, p);
  return pageQuery(q, p, all);
}
export const getPriceLog = (p: LogsParams) => call<PriceLogPage>(`/logs/price${priceQuery(p)}`);
/** Every read of the Logs tab (the three panes): a commit writes Price Log entries, so the commit bar drops them all. */
export const LOGS_KEY = ["pricing", "logs"] as const;
export const getCostsLog = (p: LogsParams) => call<CostsLogPage>(`/logs/costs${costsQuery(p)}`);
/** Every entry the filters match, in one answer, for the export (never cached: it is read once, when asked). */
export const exportPriceLog = (p: LogsParams) => call<PriceLogPage>(`/logs/price${priceQuery(p, true)}`).then(whole);
export const exportCostsLog = (p: LogsParams) => call<CostsLogPage>(`/logs/costs${costsQuery(p, true)}`).then(whole);
/** An export the API answered in part: an API older than C2 v1.35 ignores `export` and answers one page of 50. */
export class PartialExport extends Error {
  constructor(readonly read: number, readonly total: number) { super(`the export answered ${read} of ${total} entries`); }
}
/** Every matching entry or nothing: an answer with fewer rows than its total is refused, never written as a CSV. */
export function whole<T extends { rows: unknown[]; page: LogPageInfo }>(answer: T): T {
  if (answer.rows.length < answer.page.total) throw new PartialExport(answer.rows.length, answer.page.total);
  return answer;
}
/** The place filters and the view this screen sent that the answer does not echo: an API older than C2 v1.35 ignores them,
 * so the screen names them instead of showing pills over a table they did not narrow. */
export function notApplied(p: PlaceParams & { fulfilment?: FulfilmentView }, filters: object): string[] {
  const out: string[] = (["product", "variant", "channel", "store", "location", "marketplace"] as const).filter((k) => p[k] && !(k in filters)).map((k) => LOGS_COPY.filters[k]);
  if (p.fulfilment === "changes" && (filters as { fulfilment?: string }).fulfilment !== "changes") out.push(LOGS_COPY.costs.fulfilment.changes);
  return out;
}
/** Keyed by the exact query each answers, so two views with the same filters share one cache entry. */
export function priceLogQuery(p: LogsParams) {
  return { queryKey: ["pricing", "logs", "price", priceQuery(p)] as const, queryFn: () => getPriceLog(p), retry: 1, staleTime: GRID_STALE_TIME };
}
export function costsLogQuery(p: LogsParams) {
  return { queryKey: ["pricing", "logs", "costs", costsQuery(p)] as const, queryFn: () => getCostsLog(p), retry: 1, staleTime: GRID_STALE_TIME };
}

/** "Pure C8 MCT Oil · 500ml Glass (C7RET1)": the SKU filter option, product first so the list reads by product. */
export const skuLabel = (s: LogSku): string => `${s.product} · ${s.variant} (${s.xero_sku})`;
export const marketLabel = (m: Market | null): string => (m ? MARKET_LABEL[m] : "—");
/** The channel or store a row belongs to: the marketplace name on Amazon, the store name on Shopify, the location otherwise. */
export function placeLabel(row: { channel_location: string | null; marketplace_name: string | null; marketplace_id: string | null }): string {
  if (row.marketplace_name) return row.marketplace_name;
  if (row.channel_location && STORE_LABEL[row.channel_location]) return STORE_LABEL[row.channel_location];
  return row.channel_location ?? "—";
}

export const SUBJECT_LABEL: Record<PriceSubject, string> = { price: "Price", free_shipping_threshold: "Free-shipping threshold" };
export const ENTRY_LABEL: Record<CostEntryType, string> = {
  last_production_cogs: "Last Production COGS", current_inventory_cogs: "Current Inventory COGS", projected_cogs: "Projected COGS",
  avg_discount: "Avg discount cost", avg_refund: "Avg refund cost", avg_ads: "Avg ads cost", fulfilment: "Fulfilment standard",
};
/** One chip per kind of cost entry, told apart by the brand's purples and greys only: a kind is a category, never a judgement,
 * so the pass, warn and fail tones (lib/tone.ts) stay for the flags. The three kinds no feed writes yet are greys, and no label is
 * grey-1 text (3.2:1 on white). */
export const ENTRY_TONE: Record<CostEntryType, string> = {
  last_production_cogs: "border-ks-grey-3 bg-ks-surface text-ks-ink",
  current_inventory_cogs: "border-transparent bg-ks-purple-4 text-ks-purple",
  projected_cogs: "border-dashed border-ks-purple-3 bg-ks-surface text-ks-purple-shade",
  avg_discount: "border-transparent bg-ks-grey-4 text-ks-ink",
  avg_refund: "border-ks-grey-1 bg-ks-surface text-ks-ink",
  avg_ads: "border-transparent bg-ks-grey-3 text-ks-ink",
  fulfilment: "border-ks-purple bg-ks-surface text-ks-purple",
};
/** One chip per kind of change on the Price pane, in the same palette. */
export const SUBJECT_TONE: Record<PriceSubject, string> = {
  price: "border-ks-grey-3 bg-ks-surface text-ks-ink",
  free_shipping_threshold: "border-ks-purple bg-ks-surface text-ks-purple",
};
/** The feed a cost row came from, in the reader's words; an unknown source keeps its stored name. */
const COST_SOURCE_LABEL: Record<string, string> = {
  pcogs: "Production COGS feed", billed_settlement: "Fulfilment feed, billed settlement", billed_standard: "Fulfilment feed, billed standard",
  carried: "Fulfilment feed, carried forward", projection: "Fulfilment feed, projection", defined_rate: "Fulfilment feed, defined rate",
  xero_stock: "Xero stock read",
};
export const costSourceLabel = (source: string): string => COST_SOURCE_LABEL[source] ?? source;
/** "REF: COGS!499" reads "REF: COGS, row 499"; a reference with no row is shown as stored. */
export function legacyPlace(ref: string): string {
  const at = ref.lastIndexOf("!");
  return at > 0 ? `${ref.slice(0, at)}, row ${ref.slice(at + 1)}` : ref;
}
/** Where exactly a cost row came from, under its source: the production run or the legacy sheet row of a Last Production COGS
 * entry, the time and entity of the Xero read, the day PFC's run was fed. Null when C2 names none (or is older than v1.35). */
export function costOrigin(row: CostLogRow): string | null {
  const o = LOGS_COPY.costs.origin;
  if (row.source === "pcogs") {
    if (row.production_key) return o.productionRun(row.production_key);
    return row.legacy_ref ? o.legacy(legacyPlace(row.legacy_ref)) : null;
  }
  if (row.source === "xero_stock" && row.read_at) return o.xero(stamp(row.read_at), row.entity ? ENTITY_LABEL[row.entity] ?? row.entity : null);
  if (row.entry_type === "fulfilment" && row.fed_on) return o.pfc(humanDate(row.fed_on));
  return null;
}
/** "Price Run · Bojan", "Threshold run · Damien", "Legacy price sheet": the source column of the Price pane. */
export function priceSourceLabel(row: PriceLogRow): string {
  if (row.source === "legacy_sheet") return LOGS_COPY.price.legacy;
  // C1 v1.50: a return names who ended the override: a person, or the daily return call's service account in words.
  if (row.source === "override_return") return `${LOGS_COPY.price.overrideReturn} · ${enderName(row.approver)}`;
  return `${row.source === "price_run" ? LOGS_COPY.price.priceRun : LOGS_COPY.price.thresholdRun} · ${personName(row.approver)}`;
}
/** A removed free tier is logged as 0 by the C3 threshold trigger and served as "0.00" (C2 v1.6 money format); the screen names it, never shows a zero threshold. */
export const isRemovedFreeTier = (row: PriceLogRow): boolean => row.subject_type === "free_shipping_threshold" && row.new_value === "0.00";
/** A threshold entry with no previous value is a gained free tier (C8 v1.0 logs it over no previous value): it had none. */
export const hadNoFreeTier = (row: PriceLogRow): boolean => row.subject_type === "free_shipping_threshold" && row.previous_value == null;
/** The Flag filter's option text: FLAG_LABEL's reader words, with the two entries it maps to null named here so no raw code reaches the screen. */
export const flagFilterLabel = (flag: Flag): string => FLAG_LABEL[flag]?.text ?? (flag === "BILLED" ? "Billed" : "Missing");
export const componentLabel = (component: string | null): string => (component && component in COMPONENT_LABEL ? COMPONENT_LABEL[component as keyof typeof COMPONENT_LABEL] : component ?? "");

/** One RFC 4180 cell. A text cell a spreadsheet would read as a formula (=, +, -, @, a tab or a return first) gets a leading
 * apostrophe, unless it is a plain number such as "-7.9"; a cell with a comma, a quote or a line break is quoted. */
export function csvCell(value: string | number | boolean | null | undefined): string {
  let cell = value == null ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(cell) && !/^-?\d+(\.\d+)?$/.test(cell)) cell = `'${cell}`;
  return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
}
/** A whole CSV: a byte-order mark (so a spreadsheet reads £, € and Ü), the header, one line per row, every line ended CRLF. */
export function csvText(header: readonly string[], rows: ReadonlyArray<ReadonlyArray<string | number | boolean | null | undefined>>): string {
  return `\uFEFF${[header, ...rows].map((line) => line.map(csvCell).join(",")).join("\r\n")}\r\n`;
}
/** The local calendar day, "2026-10-03", for a file name. */
export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
/** "precision-pricing-costs-log-changes-2026-10-03.csv". */
export function csvName(pane: LogPane, day: string, changes = false): string {
  return `precision-pricing-${pane === "rrp" ? "rrp-exceptions" : pane}-log${changes ? "-changes" : ""}-${day}.csv`;
}
/** Hands the file to the browser, as the other modules' exports do. */
export function downloadCsv(text: string, name: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
/** The Costs Log as a CSV: every value as C2 stores it (up to four decimals), every name in the screen's words. */
export function costsCsv(rows: CostLogRow[]): string {
  const C = LOGS_COPY.costs.csv;
  return csvText(C.header, rows.map((r) => [
    r.effective_date, r.xero_sku, r.product, r.variant, MARKET_LABEL[r.market] ?? r.market,
    r.entry_type === "fulfilment" ? componentLabel(r.component) : ENTRY_LABEL[r.entry_type] ?? r.entry_type,
    r.entry_type === "fulfilment" ? placeLabel(r) : "", r.country && r.country !== "ALL" ? countryName(r.country) : "",
    r.value, r.currency, r.previous_value ?? "", flagFilterLabel(r.flag), costSourceLabel(r.source), costOrigin(r) ?? "",
    r.fed_on ?? "", r.run_id ?? "", r.record_id ?? "", r.production_key ?? "", r.legacy_ref ?? "", r.entity ?? "", r.read_at ?? "",
    r.written_at,
  ]));
}
/** The Price Log as a CSV, each change with its subject, values and source as the pane shows them. */
export function priceCsv(rows: PriceLogRow[]): string {
  const C = LOGS_COPY.price.csv;
  return csvText(C.header, rows.map((r) => [
    r.committed_at, SUBJECT_LABEL[r.subject_type] ?? r.subject_type, r.xero_sku ?? "", r.product ?? "", r.variant ?? "",
    r.channel ? CHANNEL_LABEL[r.channel as LogChannel] ?? r.channel : "", r.channel_location ? storeName(r.channel_location) : "",
    r.marketplace_name ?? (r.marketplace_id && r.marketplace_id !== "ALL" ? r.marketplace_id : ""),
    r.country ? countryName(r.country) : "", r.market ? MARKET_LABEL[r.market] : "",
    isRemovedFreeTier(r) ? LOGS_COPY.price.noFreeTier : r.new_value, r.previous_value ?? "", r.currency, personName(r.approver),
    priceSourceLabel(r), r.comment ?? "", r.price_run_id ?? "",
  ]));
}
/** Why an export was not written: too many entries (C2 names the count and the cap), or the pane's own error sentence. */
export function exportMessage(error: unknown, areaName: string): string {
  if (error instanceof PartialExport) return LOGS_COPY.export.partial(COUNT.format(error.read), COUNT.format(error.total));
  if (error instanceof PricingError && error.reason() === "too_many_rows") {
    const facts = error.facts() as { total?: number; max?: number };
    return LOGS_COPY.export.tooMany(COUNT.format(facts.total ?? 0), COUNT.format(facts.max ?? 0));
  }
  return `${LOGS_COPY.export.failed} ${messageFor(error, areaName)}`;
}

export const LOGS_COPY = {
  panes: { costs: "Costs", price: "Price", rrp: "RRP exceptions" } as Record<LogPane, string>,
  filters: { product: "Product", variant: "Variant", market: "Region", channel: "Channel", store: "Store", location: "Location", marketplace: "Marketplace", sku: "SKU" },
  noPlace: {
    costs: "Location and Marketplace list only the fee standards that have a place: an Amazon marketplace's or a ship country's. A cost entry (Last Production COGS, Current Inventory COGS) has none, so it is not listed.",
    price: "Location and Marketplace list only the prices that have a place: an Amazon marketplace's price, or a ship country's price or threshold. A store-wide Shopify price has none, so it is not listed.",
    rrp: "Location and Marketplace list only the exceptions that have a place: an Amazon marketplace's. A Shopify exception is store-wide and has none, so it is not listed.",
  } as Record<LogPane, string>,
  retry: "Retry",
  period: {
    label: "Period", wholeBook: "Whole log", start: "start", today: "today", to: "to",
    presets: { prev: "Previous month", thisMonth: "This month", last30: "Last 30 days", book: "Whole log" },
    hintStart: "Click the start day, then the end day", hintEnd: "Now click the end day",
    outside: (from: string, to: string) => `Dates outside ${from} to ${to} cannot be chosen.`,
    prevMonth: "Earlier month", nextMonth: "Later month",
  },
  export: {
    format: (total: string) => `CSV, every matching entry (${total})`,
    busy: (total: string) => `Writing the CSV of ${total} entries…`,
    failed: "The CSV was not written.",
    tooMany: (total: string, max: string) => `The CSV was not written: ${total} entries match the filters, and an export holds at most ${max}. Narrow the filters or the period and export again.`,
    partial: (read: string, total: string) => `The CSV was not written: the pricing API answered ${read} of ${total} entries, so it is older than the export.`,
  },
  noHistorySku: "No history yet for this SKU.",
  olderApi: (names: string[]) => `The pricing API is older than this screen and did not apply ${names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0]}.`,
  price: {
    title: "Price log",
    note: "Append-only history of everything committed to a channel: prices per SKU and channel and free-shipping thresholds per country, each with its previous value, who approved it and where it came from. Read only.",
    emptyTitle: "No prices committed yet",
    emptyBody: "Commit a price in a Price Run and it appears here.",
    noMatch: "No log entries match the filters",
    columns: ["Date", "Change", "SKU / Channel", "Region", "New value", "Previous", "Source"],
    filters: { market: "Region", sku: "SKU", subject: "Change", source: "Source" },
    sourceLabel: { price_run: "Price Run", threshold_run: "Threshold run", legacy_sheet: "Legacy price sheet", override_return: "Override return" } as Record<PriceSource, string>,
    priceRun: "Price Run", thresholdRun: "Threshold run", legacy: "Legacy price sheet", overrideReturn: "Override return",
    temporary: (until: string, reason: string) => `temporary price until ${until}: ${reason}`,
    returnLine: (reason: string) => `back to the price before the temporary price (${reason})`,
    country: (name: string) => `Free shipping · ${name}`,
    noFreeTier: "No free tier", first: "first value", override: (reason: string) => `below floor: ${reason}`,
    legacyNote: (comment: string) => `from ${comment}`,
    trailing: (total: string) => `${total} entries · newest first`,
    footnote: "A Price Run commit writes one entry per line with the committer as approver; a floor override keeps its reason; a temporary price keeps its reason and until date, and its return (Override return) is an entry of its own back to the price before it, written by the daily return or by End now; the line under the source is the strategy's verdict at the committed price, recorded at commit and never re-judged. A free-shipping threshold set to none is recorded as No free tier. Entries before the first commit come from the legacy price sheet import and carry no previous value and no snapshot. Product, Variant, Channel and Store are those of the entry's SKU; a free-shipping threshold has no SKU, so Product or Variant drops it, while Channel Shopify and its region's Shopify store keep it; the period is the day of the commit, in UTC.",
    csv: {
      header: ["Committed (UTC)", "Change", "SKU", "Product", "Variant", "Channel", "Store", "Marketplace", "Country", "Region",
        "New value", "Previous value", "Currency", "Approver", "Source", "Comment", "Price Run"],
    },
  },
  costs: {
    title: "Costs log",
    note: "Every cost the daily feeds wrote per SKU, newest first: production and current inventory COGS entries and the fulfilment standards per channel, marketplace, country and component, as they stand or as they changed. Read only.",
    banner: "Fulfilment entries come from the Precision Fulfilment Costs feed, written when PFC publishes a run. Last Production COGS comes from the Production COGS records and Current Inventory COGS from the Xero stock read; nothing here pulls or edits a cost. Projected COGS and the discount, refund and ads costs have no feed yet, so none is shown and no estimate stands in.",
    emptyTitle: "No cost history yet",
    emptyBody: "Entries appear after the first feeds run.",
    noMatch: "No cost entries match the filters",
    columns: ["Effective", "SKU", "Region", "Entry", "Value", "Source"],
    filters: { market: "Region", sku: "SKU", type: "Entry", flag: "Flag" },
    fulfilment: { label: "Fulfilment", current: "Current standards", changes: "Every change" } as Record<FulfilmentView, string> & { label: string },
    dated: "Date",
    effective: (day: string) => `effective ${day}`,
    was: (value: string) => `was ${value}`,
    firstFed: "first fed",
    origin: {
      productionRun: (key: string) => `Production run ${key}`,
      legacy: (place: string) => `Legacy sheet ${place}`,
      xero: (when: string, entity: string | null) => `Read ${when}${entity ? ` from ${entity}` : ""}`,
      pfc: (day: string) => `PFC run fed ${day}`,
    },
    trailing: (total: string) => `${total} entries · newest first`,
    place: (place: string, country: string | null) => (country && country !== "ALL" ? `${place} · to ${country}` : place),
    footnote: "Last Production COGS is one entry per production cost record the feed has read, dated the day the record was approved, or for records imported from the legacy sheets by the sheet's own date (the price change on REF: COGS, the shipment on the intercompany COGS calculators); an entry stays after its record is retired in Production COGS. Current Inventory COGS is the Xero average cost from the newest stock read at each feed run, one entry per SKU from its own market's entity, dated the read day; a read with no average cost for a SKU writes no entry for it, never a stand-in. Fulfilment standards show the value in force per channel, marketplace, country and component; Every change lists each standard PFC fed whose value moved by a penny or more, with the value it replaced, dated by the day it was fed, with its effective date (PFC's evidence date) under it where that differs. A flag beside a standard, such as Few orders or No sales yet, says what its value rests on; on Every change it is the flag the change was fed with, and the flag in force is on Current standards. The period is the effective date, and on Every change a fulfilment change's feed day.",
    csv: {
      header: ["Effective", "SKU", "Product", "Variant", "Region", "Entry", "Place", "Ship to", "Value", "Currency", "Previous value",
        "Flag", "Source", "Source detail", "PFC feed day", "PFC run", "Production COGS record", "Production key", "Legacy sheet row",
        "Xero entity", "Xero read at", "Written at"],
    },
  },
} as const;
